import React, { useContext, useEffect } from 'react';
import classNames from 'classnames';
import * as Classes from '../common/classes';
import { BaseProps } from '../common/props';
import type { UseQueryResult } from 'react-query';
import {
  NavBarMenuLayout,
  NavBarMenuLayoutProps,
  NavBarProps,
} from '../navbar/NavBarMenuLayout';
import { AppLayoutWaitForQueryData } from '../app/AppLayoutWaitForQueryData';
import {
  LeftPanelLayoutProps,
  SidePanelItemsPlacementEnum,
  SidePanelLayoutProps,
} from '../app/AppLayout';
import { SidePanel } from '../navbar/SidePanel';

export interface AppLayoutContextType {
  setCurrentKey: (currentKey?: string) => void;
}

export const AppLayoutContext = React.createContext<AppLayoutContextType>({
  setCurrentKey: () => {},
});

export interface ILayoutProps {
  /** Seznam class name oddělených mezerou, které jsou předané do renderované komponenty. */
  className?: string;
  /**
   * classname předávaný na nejnižší wrapper element v komponentě
   */
  contentWrapperClassName?: string;
  /**
   * Aktuální stránka, která je aktivní v layoutu v levém menu.
   */
  currentPage?: string;
  //@ts-ignore
  queries?: UseQueryResult<any, any>[];
  children: React.ReactNode | (() => React.ReactNode) | undefined;
}

export interface IAppLayoutProps extends Omit<BaseProps, 'children'> {
  /**
   * classname předávaný na nejnižší wrapper element v komponentě
   */
  contentWrapperClassName?: string;
  queries?: UseQueryResult<any, any>[];
  children: React.ReactNode | (() => React.ReactNode) | undefined;
}

const defaultLeftPanelWidth = 210;

export type AppLayoutProps = IAppLayoutProps &
  NavBarMenuLayoutProps &
  SidePanelLayoutProps &
  LeftPanelLayoutProps;

export const ActivePage: React.FC<{ currentPage?: string }> = ({
  currentPage,
}) => {
  const appLayoutContext = useContext(AppLayoutContext);

  useEffect(() => {
    appLayoutContext.setCurrentKey(currentPage);
  }, [currentPage]);

  return <></>;
};

export const AppLayout: React.FC<AppLayoutProps> = ({
  children,
  className,
  queries,
  sidePanel,
  leftPanel,
  navbar,
  contentWrapperClassName,
}) => {
  const [currentKey, setCurrentKey] = React.useState<string | undefined>(
    undefined
  );

  let cls = classNames(Classes.APP_LAYOUT, className, {});

  const contentWrapperCls = classNames(
    Classes.APP_LAYOUT_CONTENT_WRAPPER,
    contentWrapperClassName
  );

  const sidePanelLocation =
    sidePanel?.itemsPlacement || SidePanelItemsPlacementEnum.IN_NAVBAR;

  let navbarProps: NavBarProps = {
    ...navbar,
    // @ts-ignore
    appMenu: { ...navbar?.appMenu, currentKey: currentKey },
  };

  return (
    <AppLayoutContext.Provider value={{ setCurrentKey }}>
      <div className={cls}>
        <NavBarMenuLayout
          sidePanel={
            sidePanelLocation === SidePanelItemsPlacementEnum.IN_NAVBAR
              ? sidePanel
              : undefined
          }
          navbar={navbarProps}
        >
          <AppLayoutWaitForQueryData queries={queries ?? []}>
            {() => (
              <div className={Classes.APP_LAYOUT_NAVBAR_CONTENT_WRAPPER}>
                {leftPanel && (
                  <div
                    style={{
                      width: leftPanel.panelWidth || defaultLeftPanelWidth,
                    }}
                    className={classNames(
                      Classes.APP_LAYOUT_LEFT_PANEL,
                      leftPanel.className
                    )}
                  >
                    {leftPanel.content}
                  </div>
                )}
                <div className={contentWrapperCls}>
                  {typeof children === 'function' ? children() : children}
                </div>
              </div>
            )}
          </AppLayoutWaitForQueryData>
        </NavBarMenuLayout>
        {sidePanel &&
          sidePanelLocation === SidePanelItemsPlacementEnum.SIDE && (
            <SidePanel {...sidePanel} />
          )}
      </div>
    </AppLayoutContext.Provider>
  );
};
